/**
 * Product telemetry — what happened, never who (R11, ADR-038).
 *
 * Every field here is a bucket or a public code. There is no uid, no email, no
 * résumé text, no free text at all. That is not a convention to be careful
 * about: `firestore.rules` refuses any write carrying a field outside the
 * allowlist, so the guarantee survives a careless addition six months from now.
 *
 * Written by ANYONE, signed in or not. The whole free journey is anonymous, and
 * telemetry covering only signed-in users would describe the minority who got
 * past the wall and quietly mislead about everyone else — which is the opposite
 * of what the admin console is for.
 *
 * Values are banded rather than exact so that no row is distinctive enough to
 * be a fingerprint. An exposure score of 5.7 plus an occupation plus a timestamp
 * starts to identify a person; "moderate exposure, IT project manager, today"
 * does not.
 */
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";

const band = (n, cuts, labels) => {
  if (typeof n !== "number") return null;
  for (let i = 0; i < cuts.length; i++) if (n < cuts[i]) return labels[i];
  return labels[labels.length - 1];
};

export const exposureBand = (n) => band(n, [4, 7], ["low", "moderate", "high"]);
export const confidenceBand = (n) => band(n, [0.5, 0.7, 0.9], ["very-low", "low", "good", "high"]);

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Record one product event. Never throws and never blocks the UI — telemetry
 * that can break the thing it measures is worse than no telemetry.
 */
export async function recordSignal(event, fields = {}) {
  if (!db || !event) return;
  try {
    await addDoc(collection(db, "signals"), {
      event,
      day: today(),
      at: serverTimestamp(),
      schema: 1,
      // Only ever these. Anything else is refused by the rules.
      ...(fields.occupationCode ? { occupationCode: fields.occupationCode } : {}),
      ...(fields.exposureBand ? { exposureBand: fields.exposureBand } : {}),
      ...(fields.confidenceBand ? { confidenceBand: fields.confidenceBand } : {}),
      ...(fields.resolvedVia ? { resolvedVia: fields.resolvedVia } : {}),
      ...(typeof fields.escalated === "boolean" ? { escalated: fields.escalated } : {}),
      ...(fields.targetCode ? { targetCode: fields.targetCode } : {}),
      ...(fields.contentId ? { contentId: fields.contentId } : {}),
    });
  } catch (e) {
    // Swallowed on purpose. A blocked write, an offline device or a rules
    // change must never surface to someone trying to use the product.
    if (import.meta.env.DEV) console.warn("[signals]", event, e?.code || e);
  }
}

// The vocabulary lives in a pure module so the suite can load it without
// pulling in Firebase; re-exported here so existing imports keep working.
export { EVENTS, deltaEvent } from "./events.js";
